import { apiFetch } from '@/api/request'
import type { ApiResponse } from '@/types/verification'

export interface MallShippingAddress {
  idx: number
  label: string
  receiver: string
  phone: string
  zipcode: string
  address: string
  addressDetail: string
  isDefault: boolean
  createdAt?: string
  updatedAt?: string | null
}

export interface MallShippingPref {
  lastMemo: string
  /** 최근 주문·선호에 저장된 입금자명. 없으면 빈 문자열 */
  lastDepositor?: string
}

export interface MallShippingPayload {
  label?: string
  receiver: string
  phone: string
  zipcode?: string
  address: string
  addressDetail?: string
  isDefault?: boolean
}

const API_ORIGIN = (process.env.VUE_APP_API_ORIGIN || '').replace(/\/$/, '')
const BASE = `${API_ORIGIN}/api/mall`

const fetchOptions: RequestInit = { credentials: 'include' }
const jsonOptions: RequestInit = {
  ...fetchOptions,
  headers: { 'Content-Type': 'application/json' },
}

async function parseJson<T>(response: Response): Promise<ApiResponse<T>> {
  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    throw new Error('API 서버에 연결할 수 없습니다. 백엔드와 프론트 dev 서버를 확인해 주세요.')
  }

  const data = (await response.json()) as ApiResponse<T>
  if (!response.ok && !data.message) {
    throw new Error('요청 처리에 실패했습니다.')
  }
  return data
}

function unwrap<T>(result: ApiResponse<T>, fallback: string): T {
  if (!result.success || !result.data) {
    throw new Error(result.message || fallback)
  }
  return result.data
}

export async function fetchShippingBook() {
  const response = await apiFetch(`${BASE}/shipping-addresses`, fetchOptions)
  const result = await parseJson<{ addresses: MallShippingAddress[]; pref: MallShippingPref }>(response)
  return unwrap(result, '배송지를 불러오지 못했습니다.')
}

export async function createShippingAddress(payload: MallShippingPayload) {
  const response = await apiFetch(`${BASE}/shipping-addresses`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify(payload),
  })
  const result = await parseJson<{ address: MallShippingAddress }>(response)
  return unwrap(result, '배송지 저장에 실패했습니다.').address
}

export async function updateShippingAddress(idx: number, payload: MallShippingPayload) {
  const response = await apiFetch(`${BASE}/shipping-addresses/${idx}`, {
    ...jsonOptions,
    method: 'PUT',
    body: JSON.stringify(payload),
  })
  const result = await parseJson<{ address: MallShippingAddress }>(response)
  return unwrap(result, '배송지 수정에 실패했습니다.').address
}

export async function deleteShippingAddress(idx: number) {
  const response = await apiFetch(`${BASE}/shipping-addresses/${idx}`, {
    ...fetchOptions,
    method: 'DELETE',
  })
  const result = await parseJson<null>(response)
  if (!result.success) {
    throw new Error(result.message || '배송지를 삭제하지 못했습니다.')
  }
}

export async function saveShippingMemo(memo: string) {
  const response = await apiFetch(`${BASE}/shipping-memo`, {
    ...jsonOptions,
    method: 'PUT',
    body: JSON.stringify({ memo }),
  })
  const result = await parseJson<{ pref: MallShippingPref }>(response)
  return unwrap(result, '요청사항 저장에 실패했습니다.').pref
}
